import React, { useEffect, useState } from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import {
	useRelayEnvironment,
	ConnectionHandler,
	commitLocalUpdate,
	fetchQuery,
	graphql,
	useFragment,
	useMutation,
	useQueryLoader,
} from 'react-relay';
import type { RecordProxy, RecordSourceSelectorProxy } from 'relay-runtime';
import Button, { ButtonGroup } from '@atlaskit/button';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { Box, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { JiraVersionAri } from '@atlassian/ari/jira/version';
import ComponentWithAnalytics from '@atlassian/jira-analytics-web-react/src/utils/component-with-analytics.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useFlagsService, type FlagMessage } from '@atlassian/jira-flags';
import { type MessageDescriptor, type MessageDescriptorV2, useIntl } from '@atlassian/jira-intl';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { type UIAnalyticsEvent, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { projectContextResource } from '@atlassian/jira-router-resources-project-context/src/services/project-context/index.tsx';
import { isDevopsFeatureDisabledInFedRamp } from '@atlassian/jira-software-devops-fedramp-utils/src/index.tsx';
import { ReleaseNotesModal } from '@atlassian/jira-software-releases-add-release-notes-modal/src/ui/async.tsx';
import { useEditReleaseExperienceStart } from '@atlassian/jira-software-releases-release-modals-relay/src/services/edit-release-experience-tracker/index.tsx';
import { useMergeReleaseExperienceStart } from '@atlassian/jira-software-releases-release-modals-relay/src/services/merge-release-experience-tracker/index.tsx';
import { EditReleaseModal } from '@atlassian/jira-software-releases-release-modals-relay/src/ui/edit-release-modal/async.tsx';
import { MergeReleaseModal } from '@atlassian/jira-software-releases-release-modals-relay/src/ui/merge-release-modal/async.tsx';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import type {
	Version,
	SuccessParams,
} from '@atlassian/jira-version-modals/src/common/model/index.tsx';
import DeleteModal from '@atlassian/jira-version-modals/src/delete-version/index.tsx';
import ReleaseModal from '@atlassian/jira-version-modals/src/release-version-modal/index.tsx';
import { useResource } from '@atlassian/react-resource-router';
import RelayDataID from '@atlassian/relay-data-id';
import { fg } from '@atlassian/jira-feature-gating';
import editReleaseModalQuery, {
	type editReleaseModalQuery as editReleaseModalQueryType,
} from '@atlassian/jira-software-releases-release-modals-relay/src/ui/edit-release-modal/__generated__/editReleaseModalQuery.graphql';
import RELEASE_NOTES_QUERY, {
	type uiReleaseNotesModalQuery,
} from '@atlassian/jira-software-releases-add-release-notes-modal/src/ui/__generated__/uiReleaseNotesModalQuery.graphql';
import mergeReleaseModalQuery, {
	type mergeReleaseModalQuery as mergeReleaseModalQueryType,
} from '@atlassian/jira-software-releases-release-modals-relay/src/ui/merge-release-modal/__generated__/mergeReleaseModalQuery.graphql';
import {
	UNRELEASED,
	ARCHIVED,
	RELEASED,
	PROJECT_VERSIONS_ANALYTICS_SOURCE,
} from '../../../common/constants.tsx';
import type { actions_versionHasReleaseNoteQuery } from './__generated__/actions_versionHasReleaseNoteQuery.graphql';
import type { actions_updateVersionReleasedMutation } from './__generated__/actions_updateVersionReleasedMutation.graphql';
import type { actions_updateVersionArchivedMutation } from './__generated__/actions_updateVersionArchivedMutation.graphql';
import type { actions_projectVersions_ActionsCell_jiraVersion$key } from './__generated__/actions_projectVersions_ActionsCell_jiraVersion.graphql';
import type { actions_projectVersions_ActionsCell_jiraProject$key } from './__generated__/actions_projectVersions_ActionsCell_jiraProject.graphql';
import type { actions_deleteVersionWithNoIssuesMutation } from './__generated__/actions_deleteVersionWithNoIssuesMutation.graphql';
import messages from './messages.tsx';

const DropdownItemWithAnalytics = ComponentWithAnalytics('dropdownItem', {
	onClick: 'clicked',
})(DropdownItem);

export type Props = {
	jiraVersion: actions_projectVersions_ActionsCell_jiraVersion$key;
	jiraProject: actions_projectVersions_ActionsCell_jiraProject$key;
	showSpinner: boolean;
	connectionId: string | null;
};

const getHasReleaseNote = async (
	versionId: string,
	environment: ReturnType<typeof useRelayEnvironment>,
) => {
	try {
		const data = await fetchQuery<actions_versionHasReleaseNoteQuery>(
			environment,
			// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
			graphql`
				query actions_versionHasReleaseNoteQuery($versionId: ID!) {
					jira {
						version(id: $versionId) {
							... on JiraVersion {
								hasReleaseNote
							}
						}
					}
				}
			`,
			{ versionId },
		).toPromise();
		return data?.jira?.version?.hasReleaseNote ?? false;
	} catch (error) {
		fireErrorAnalytics({
			meta: {
				id: 'jiraProjectVersionsHasReleaseNoteQuery',
				packageName: 'jiraProjectVersions',
				teamName: 'jolt',
			},
			error: new Error('Unable to get "hasReleaseNote" for "release version" modal'),
		});
		return false;
	}
};

const useHasReleaseNote = (
	versionId: string,
	shouldLoadHasReleaseNote: boolean,
): boolean | null => {
	const [hasReleaseNote, setHasReleaseNote] = useState<boolean | null>(null);
	const environment = useRelayEnvironment();
	useEffect(() => {
		if (shouldLoadHasReleaseNote) {
			getHasReleaseNote(versionId, environment).then(setHasReleaseNote);
		}
	}, [versionId, environment, shouldLoadHasReleaseNote]);
	return hasReleaseNote;
};

const ActionsCell = ({
	jiraVersion,
	jiraProject,
	showSpinner,
	connectionId,
}: Props): React.JSX.Element => {
	const version = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment actions_projectVersions_ActionsCell_jiraVersion on JiraVersion {
				__id @required(action: THROW)
				id @required(action: THROW)
				name @required(action: THROW)
				versionId @required(action: THROW)
				status @required(action: THROW)
				allIssues: issues(filter: ALL) @required(action: THROW) {
					totalCount @required(action: THROW)
				}
				doneIssues: issues(filter: DONE) @required(action: THROW) {
					totalCount @required(action: THROW)
				}
				...mergeReleaseModal_softwareReleasesReleaseModalsRelay_jiraVersion
			}
		`,
		jiraVersion,
	);

	const project = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment actions_projectVersions_ActionsCell_jiraProject on JiraProject
			@argumentDefinitions(
				isManageVersionsPermissionEnabled: { type: "Boolean", defaultValue: false }
			) {
				key @required(action: THROW)
				id @required(action: THROW)
				canEditVersions: action(type: MANAGE_VERSIONS)
					@include(if: $isManageVersionsPermissionEnabled) {
					canPerform @required(action: THROW)
				}
			}
		`,
		jiraProject,
	);

	const { formatMessage } = useIntl();
	const { showFlag } = useFlagsService();
	const locale = useLocale();
	const environment = useRelayEnvironment();
	const cloudId = useCloudId();
	const activationId = useActivationId();
	const projectContext = useResource(projectContextResource);
	const canEditVersions =
		fg('vmc-11-manage-versions-permission') && project?.canEditVersions?.canPerform;
	const isProjectAdmin = projectContext?.data?.isProjectAdmin || false;
	const isSimplified = projectContext?.data?.simplified || false;
	const projectKey = project.key;
	const projectAri = project.id;

	const [editReleaseFormQueryRef, loadEditReleaseModalQuery] =
		useQueryLoader<editReleaseModalQueryType>(editReleaseModalQuery);

	const [releaseNotesQueryRef, loadReleaseNotesQuery] =
		useQueryLoader<uiReleaseNotesModalQuery>(RELEASE_NOTES_QUERY);

	const [mergeReleaseFormQueryRef, loadMergeReleaseModalQuery] =
		useQueryLoader<mergeReleaseModalQueryType>(mergeReleaseModalQuery);

	const loadEditQuery = () => {
		if (!editReleaseFormQueryRef) {
			loadEditReleaseModalQuery({
				versionAri: version.id,
			});
		}
	};

	const loadCreateReleaseNotesQuery = () => {
		if (!releaseNotesQueryRef) {
			loadReleaseNotesQuery({ versionAri: version.id }, { fetchPolicy: 'store-and-network' });
		}
	};

	const loadMergeQuery = () => {
		if (!mergeReleaseFormQueryRef) {
			loadMergeReleaseModalQuery({
				projectAri,
			});
		}
	};

	const editExperienceStart = useEditReleaseExperienceStart(PROJECT_VERSIONS_ANALYTICS_SOURCE);
	const mergeExperienceStart = useMergeReleaseExperienceStart(PROJECT_VERSIONS_ANALYTICS_SOURCE);

	// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
	const [updateVersionReleased] = useMutation<actions_updateVersionReleasedMutation>(graphql`
		mutation actions_updateVersionReleasedMutation($input: JiraUpdateVersionReleasedStatusInput!) {
			jira {
				updateVersionReleasedStatus(input: $input) @optIn(to: ["JiraUpdateVersionReleasedStatus"]) {
					success
					version {
						status
					}
				}
			}
		}
	`);

	// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
	const [updateVersionArchived] = useMutation<actions_updateVersionArchivedMutation>(graphql`
		mutation actions_updateVersionArchivedMutation($input: JiraUpdateVersionArchivedStatusInput!) {
			jira {
				updateVersionArchivedStatus(input: $input) @optIn(to: ["JiraUpdateVersionArchivedStatus"]) {
					success
					version {
						status
					}
				}
			}
		}
	`);

	const [deleteVersionWithNoIssues] = useMutation<actions_deleteVersionWithNoIssuesMutation>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			mutation actions_deleteVersionWithNoIssuesMutation(
				$input: JiraDeleteVersionWithNoIssuesInput!
			) {
				jira {
					deleteJiraVersionWithNoIssues(input: $input) @optIn(to: ["JiraDeteVersionWithNoIssues"]) {
						success
					}
				}
			}
		`,
	);

	const [editVersionModalOpen, setEditVersionModalOpen] = useState(false);
	const [isReleaseModalOpen, setIsReleaseModalOpen] = useState(false);
	const [isReleaseNotesModalOpen, setIsReleaseNotesModalOpen] = useState<boolean>(false);
	const [isDeleteVersionModalOpen, setIsDeleteVersionModalOpen] = useState<boolean>(false);
	const [mergeVersionModalOpen, setMergeVersionModalOpen] = useState<boolean>(false);

	const hasReleaseNote = useHasReleaseNote(version.id, isReleaseModalOpen);

	const canMergeVersions = () => !isSimplified;
	const canBeReleased = () => version.status !== 'RELEASED' && version.status !== 'ARCHIVED';
	const canBeArchived = () => version.status !== 'ARCHIVED';
	const canBeUnreleased = () => version.status === 'RELEASED';
	const canDelete = () =>
		fg('vmc-11-manage-versions-permission') ? canEditVersions : isProjectAdmin;
	const canQuickRelease = () =>
		version.status === 'UNRELEASED' &&
		version.allIssues.totalCount > 0 &&
		version.allIssues.totalCount === version.doneIssues.totalCount;

	const onReleaseClicked = () => {
		if (!isDevopsFeatureDisabledInFedRamp()) {
			loadCreateReleaseNotesQuery();
		}
		setIsReleaseModalOpen(true);
	};

	const reportError = ({
		metaId,
		errorMessage,
		messageId,
		title,
		description,
	}: {
		metaId: string;
		errorMessage: string;
		messageId: string;
		title: string | FlagMessage<MessageDescriptorV2>;
		description?: string | FlagMessage<MessageDescriptorV2>;
	}) => {
		fireErrorAnalytics({
			meta: {
				id: metaId,
				packageName: 'jiraProjectVersions',
				teamName: 'jolt',
			},
			error: new Error(errorMessage),
			sendToPrivacyUnsafeSplunk: true,
		});
		showFlag({
			type: 'error',
			title,
			description,
			messageId,
			messageType: 'transactional',
		});
	};

	const onUnreleaseClicked = () => {
		updateVersionReleased({
			variables: { input: { id: version.id, isReleased: false } },
			onCompleted: (data) => {
				const success = data?.jira?.updateVersionReleasedStatus?.success;
				if (success || !fg('jira-versions-list-success-false-error-handling')) {
					showFlag({
						type: 'success',
						title: [messages.unReleaseTitle, { name: version.name }],
						messageId:
							'project-versions.ui.table.actions.show-flag.success.on-unrelease-clicked.on-completed.show-flag.success',
						messageType: 'transactional',
					});
				} else {
					reportError({
						metaId: 'jiraProjectVersionsUnReleaseMutation',
						errorMessage: 'Update version released status returned success=false',
						messageId:
							'project-versions.ui.table.actions.show-flag.error.on-unrelease-clicked.on-completed.success-false',
						title: messages.genericUpdateFailedFlagTitle,
						description: messages.releaseFailedFlagDescription,
					});
				}
			},
			onError: () => {
				reportError({
					metaId: 'jiraProjectVersionsUnReleaseMutation',
					errorMessage: 'Unable to update version released status',
					messageId:
						'project-versions.ui.table.actions.show-flag.error.on-unrelease-clicked.on-error.show-flag.error',
					title: messages.genericUpdateFailedFlagTitle,
					description: messages.releaseFailedFlagDescription,
				});
			},
			optimisticResponse: {
				jira: {
					updateVersionReleasedStatus: {
						success: true,
						version: {
							id: version.id,
							status: UNRELEASED,
						},
					},
				},
			},
		});
	};

	const onArchiveClicked = () => {
		updateVersionArchived({
			variables: {
				input: {
					id: version.id,
					isArchived: true,
				},
			},
			onCompleted: (data) => {
				const success = data?.jira?.updateVersionArchivedStatus?.success;
				if (success || !fg('jira-versions-list-success-false-error-handling')) {
					showFlag({
						type: 'success',
						title: [messages.archiveTitle, { name: version.name }],
						messageId:
							'project-versions.ui.table.actions.show-flag.success.on-archive-clicked.on-completed.show-flag.success',
						messageType: 'transactional',
					});
				} else {
					reportError({
						metaId: 'jiraProjectVersionsArchiveMutation',
						errorMessage: 'Update version archived status returned success=false',
						messageId:
							'project-versions.ui.table.actions.show-flag.error.on-archive-clicked.on-completed.success-false',
						title: messages.genericUpdateFailedFlagTitle,
						description: messages.archiveFailedFlagDescription,
					});
				}
			},
			optimisticResponse: {
				jira: {
					updateVersionArchivedStatus: {
						success: true,
						version: {
							id: version.id,
							status: ARCHIVED,
						},
					},
				},
			},
			onError: () => {
				reportError({
					metaId: 'jiraProjectVersionsArchiveMutation',
					errorMessage: 'Unable to update version archived status',
					messageId:
						'project-versions.ui.table.actions.show-flag.error.on-archive-clicked.on-error.show-flag.error',
					title: messages.genericUpdateFailedFlagTitle,
					description: messages.archiveFailedFlagDescription,
				});
			},
		});
	};

	const onUnarchiveClicked = () => {
		updateVersionArchived({
			variables: {
				input: {
					id: version.id,
					isArchived: false,
				},
			},
			onCompleted: (data) => {
				const success = data?.jira?.updateVersionArchivedStatus?.success;
				if (success || !fg('jira-versions-list-success-false-error-handling')) {
					showFlag({
						type: 'success',
						title: [messages.unarchiveTitle, { name: version.name }],
						messageId:
							'project-versions.ui.table.actions.show-flag.success.on-unarchive-clicked.on-completed.show-flag.success',
						messageType: 'transactional',
					});
				} else {
					reportError({
						metaId: 'jiraProjectVersionsUnArchiveMutation',
						errorMessage: 'Update version archived status returned success=false',
						messageId:
							'project-versions.ui.table.actions.show-flag.error.on-unarchive-clicked.on-completed.success-false',
						title: messages.genericUpdateFailedFlagTitle,
						description: messages.archiveFailedFlagDescription,
					});
				}
			},
			optimisticResponse: {
				jira: {
					updateVersionArchivedStatus: {
						success: true,
						version: {
							id: version.id,
							status: UNRELEASED,
						},
					},
				},
			},
			onError: () => {
				reportError({
					metaId: 'jiraProjectVersionsUnArchiveMutation',
					errorMessage: 'Unable to update version archived status',
					messageId:
						'project-versions.ui.table.actions.show-flag.error.on-unarchive-clicked.on-error.show-flag.error',
					title: messages.genericUpdateFailedFlagTitle,
					description: messages.archiveFailedFlagDescription,
				});
			},
		});
	};

	const onMergeClicked = () => {
		mergeExperienceStart();
		setMergeVersionModalOpen(true);
	};

	const onEditClicked = () => {
		editExperienceStart();
		setEditVersionModalOpen(true);
	};

	const onDeleteClicked = () => {
		if (version.allIssues.totalCount) {
			setIsDeleteVersionModalOpen(true);
		} else {
			deleteVersionWithNoIssues({
				variables: { input: { id: version.id } },
				optimisticUpdater: updateStoreOnDeleteVersionWithNoIssues,
				updater: updateStoreOnDeleteVersionWithNoIssues,
				onCompleted: (data) => {
					const success = data?.jira?.deleteJiraVersionWithNoIssues?.success;
					if (success || !fg('jira-versions-list-success-false-error-handling')) {
						showFlag({
							type: 'success',
							title: formatMessage(messages.deleteTitle, { name: version.name }),
							testId: 'project-directories.versions.flags.delete.success',
							messageId:
								'project-versions.ui.table.actions.show-flag.success.on-delete-clicked.on-completed.show-flag.success',
							messageType: 'transactional',
						});
					} else {
						reportError({
							metaId: 'jiraProjectVersionsDeleteVersionWithNoIssuesMutation',
							errorMessage: 'Unable to delete version',
							messageId:
								'project-versions.ui.table.actions.show-flag.error.on-delete-clicked.on-error.show-flag.error',
							title: messages.genericUpdateFailedFlagTitle,
							description: messages.deleteFailedDescription,
						});
					}
				},
				onError: () => {
					reportError({
						metaId: 'jiraProjectVersionsDeleteVersionWithNoIssuesMutation',
						errorMessage: 'Unable to delete version',
						messageId:
							'project-versions.ui.table.actions.show-flag.error.on-delete-clicked.on-error.show-flag.error',
						title: messages.genericUpdateFailedFlagTitle,
						description: messages.deleteFailedDescription,
					});
				},
			});
		}
	};

	const updateStoreOnReleased = () => {
		commitLocalUpdate(environment, (store) => {
			const dataID = RelayDataID({ id: version.id }, 'JiraVersion') ?? '';
			const versionRecord = store.get(dataID);

			if (versionRecord) {
				versionRecord.setValue(RELEASED, 'status');
			}
		});
	};

	const updateStoreOnDelete = (targetVersionId: string | undefined): void => {
		commitLocalUpdate(environment, (store) => {
			const getVersionLinkedRecordByStatus = (record: RecordProxy, status: string): RecordProxy =>
				record.getLinkedRecord(`issues(filter:"${status}")`);

			const getVersionIssueCountByStatus = (record: RecordProxy, status: string): number =>
				Number(getVersionLinkedRecordByStatus(record, status)?.getValue('totalCount')) || 0;

			if (connectionId) {
				const connectionRecord = store.get(connectionId);

				if (connectionRecord) {
					if (targetVersionId) {
						const targetVersionAri = JiraVersionAri.create({
							siteId: cloudId,
							activationId,
							versionId: targetVersionId,
						}).toString();

						const targetVersionRecord = store.get(
							RelayDataID({ id: targetVersionAri }, 'JiraVersion') ?? '',
						);

						const sourceVersionRecord = store.get(
							RelayDataID({ id: version.id }, 'JiraVersion') ?? '',
						);

						if (targetVersionRecord && sourceVersionRecord) {
							['ALL', 'TODO', 'IN_PROGRESS', 'DONE'].forEach((status) => {
								getVersionLinkedRecordByStatus(targetVersionRecord, status).setValue(
									getVersionIssueCountByStatus(targetVersionRecord, status) +
										getVersionIssueCountByStatus(sourceVersionRecord, status),
									'totalCount',
								);
							});
						}
					}

					ConnectionHandler.deleteNode(connectionRecord, version.__id);
				}
			}
		});
	};

	const updateStoreOnDeleteVersionWithNoIssues = (store: RecordSourceSelectorProxy) => {
		const deletedVersionRecord = store.get(RelayDataID({ id: version.id }, 'JiraVersion') ?? '');

		if (deletedVersionRecord) {
			store.delete(deletedVersionRecord.getDataID());
		}
	};

	const renderReleaseButton = () => {
		if (!canQuickRelease()) {
			return null;
		}

		return (
			<ButtonGroupItem>
				<Button isDisabled={showSpinner} onClick={onReleaseClicked}>
					{formatMessage(messages.releaseAction)}
				</Button>
			</ButtonGroupItem>
		);
	};

	const renderDropdownItem = (
		action: () => void,
		message: MessageDescriptor,
		analyticsEventTitle: string,
		href?: string,
		testId?: string,
		disabledTooltipMessage?: MessageDescriptor,
		preload?: () => void,
	) => {
		const otherProps = testId ? { 'data-test-id': testId } : {};

		if (disabledTooltipMessage) {
			return (
				<Tooltip content={formatMessage(disabledTooltipMessage)}>
					<DropdownItem
						onClick={action}
						isDisabled
						href={href}
						{...otherProps}
						UNSAFE_shouldDisableRouterLink
					>
						{formatMessage(message)}
					</DropdownItem>
				</Tooltip>
			);
		}

		return (
			<div onFocus={preload || undefined} onMouseOver={preload || undefined}>
				<DropdownItemWithAnalytics
					// @ts-expect-error - Type '(_e: React.MouseEvent<HTMLElement, MouseEvent>, analyticsEvent: UIAnalyticsEvent) => void' is not assignable to type '(e: MouseEvent<Element, MouseEvent> | KeyboardEvent<Element>, args_1: UIAnalyticsEvent) => any'.
					onClick={(
						_e: React.MouseEvent<HTMLElement, MouseEvent>,
						analyticsEvent: UIAnalyticsEvent,
					) => {
						fireUIAnalytics(analyticsEvent, analyticsEventTitle);
						action();
					}}
					href={href}
					{...otherProps}
				>
					{formatMessage(message)}
				</DropdownItemWithAnalytics>
			</div>
		);
	};

	const renderDropdownGroup = () => {
		if (!canBeArchived()) {
			const innerContent = (
				<>
					{renderDropdownItem(onUnarchiveClicked, messages.unarchiveAction, 'unarchive')}
					{canMergeVersions()
						? renderDropdownItem(
								onMergeClicked,
								messages.mergeAction,
								'merge',
								undefined,
								undefined,
								undefined,
								loadMergeQuery,
							)
						: null}
					{renderDropdownItem(
						onEditClicked,
						messages.editAction,
						'edit',
						undefined,
						undefined,
						undefined,
						loadEditQuery,
					)}
					{renderDropdownItem(
						onDeleteClicked,
						messages.deleteAction,
						'delete',
						undefined,
						undefined,
						!canDelete() ? messages.deleteActionDisabledTooltip : undefined,
					)}
				</>
			);
			return <DropdownItemGroup>{innerContent}</DropdownItemGroup>;
		}
		const innerContentDefault = (
			<>
				{!canQuickRelease() && canBeReleased()
					? renderDropdownItem(
							onReleaseClicked,
							messages.releaseAction,
							'release',
							undefined,
							'project-directories.versions.main.release-version-item',
						)
					: null}
				{canBeUnreleased()
					? renderDropdownItem(onUnreleaseClicked, messages.unreleaseAction, 'unrelease')
					: null}
				{renderDropdownItem(onArchiveClicked, messages.archiveAction, 'archive')}
				{canMergeVersions()
					? renderDropdownItem(
							onMergeClicked,
							messages.mergeAction,
							'merge',
							undefined,
							undefined,
							undefined,
							loadMergeQuery,
						)
					: null}
				{renderDropdownItem(
					onEditClicked,
					messages.editAction,
					'edit',
					undefined,
					undefined,
					undefined,
					loadEditQuery,
				)}
				{renderDropdownItem(
					onDeleteClicked,
					messages.deleteAction,
					'delete',
					undefined,
					'project-directories.versions.main.delete-version-item',
					!canDelete() ? messages.deleteActionDisabledTooltip : undefined,
				)}
			</>
		);
		return <DropdownItemGroup>{innerContentDefault}</DropdownItemGroup>;
	};

	const IconBefore = showSpinner ? (
		<Box xcss={spinnerContainerStyles}>
			<Spinner size="small" />
		</Box>
	) : (
		<MoreIcon
			spacing="spacious"
			label={formatMessage(messages.moreActionsLabel, { versionName: version.name })}
		/>
	);

	return (
		<ActionsCellWrapper>
			<ButtonGroup>
				{renderReleaseButton()}
				<ButtonGroupItem>
					<DropdownMenu
						shouldRenderToParent
						placement="bottom-end"
						defaultOpen={false}
						trigger={({ triggerRef, onClick, ...props }) => (
							<Tooltip
								hideTooltipOnClick
								content={formatMessage(messages.moreActionsTooltip)}
								position="bottom"
							>
								<Button
									{...props}
									onClick={(event, analyticsEvent) => {
										fireUIAnalytics(analyticsEvent, 'versionAction');
										if (onClick != null) {
											onClick(event);
										}
									}}
									ref={triggerRef}
									appearance="subtle"
									iconBefore={IconBefore}
									isDisabled={showSpinner}
								/>
							</Tooltip>
						)}
					>
						{renderDropdownGroup()}
					</DropdownMenu>
				</ButtonGroupItem>
			</ButtonGroup>
			<>
				{editReleaseFormQueryRef && editVersionModalOpen && (
					<EditReleaseModal
						versionId={version.versionId}
						isOpen={editVersionModalOpen}
						onClose={() => setEditVersionModalOpen(false)}
						queryReference={editReleaseFormQueryRef}
					/>
				)}
			</>
			<JSErrorBoundary
				fallback="flag"
				packageName="jiraProjectVersions"
				teamName="jolt"
				id="releasesReleaseNotesModal"
			>
				<ModalTransition>
					<Placeholder name="release-notes-modal" fallback={null}>
						{isReleaseNotesModalOpen && releaseNotesQueryRef && (
							<ReleaseNotesModal
								queryReference={releaseNotesQueryRef}
								onClose={() => setIsReleaseNotesModalOpen(false)}
							/>
						)}
					</Placeholder>
				</ModalTransition>
			</JSErrorBoundary>
			<ModalTransition>
				{isReleaseModalOpen && hasReleaseNote !== null && (
					<ReleaseModal
						// @ts-expect-error - TS2322 - Type '{ locale: Locale; projectKey: string; versionId: string; onOpened: (...args: any[]) => void; onCancel: () => void; onSuccess: () => void; onError: () => void; }' is not assignable to type 'IntrinsicAttributes & StateProps & Handlers & { children?: ReactNode; }'.
						locale={locale}
						projectKey={project.key}
						versionId={version.versionId}
						onOpened={noop}
						onCancel={() => setIsReleaseModalOpen(false)}
						onSuccess={(_updatedVersion: Version, params: SuccessParams | undefined) => {
							setIsReleaseModalOpen(false);
							updateStoreOnReleased();
							if (params && params.shouldRedirectToCreateReleaseNotes) {
								setIsReleaseNotesModalOpen(true);
							}
						}}
						onError={() => {
							reportError({
								metaId: 'jiraProjectVersionsReleaseModal',
								errorMessage: 'Failed to release the version',
								messageId:
									'project-versions.ui.table.actions.show-flag.error.release-modal.on-error.show-flag.error',
								title: messages.genericUpdateFailedFlagTitle,
								description: messages.releaseFailedFlagDescription,
							});
						}}
						shouldShowCreateReleaseNotesOption={!isDevopsFeatureDisabledInFedRamp()}
						hasReleaseNote={hasReleaseNote ?? false}
					/>
				)}
			</ModalTransition>
			{mergeReleaseFormQueryRef && mergeVersionModalOpen && (
				<MergeReleaseModal
					jiraVersion={version}
					queryReference={mergeReleaseFormQueryRef}
					onClose={() => setMergeVersionModalOpen(false)}
				/>
			)}
			{isDeleteVersionModalOpen && (
				<JSErrorBoundary
					fallback="flag"
					packageName="jiraProjectVersions"
					teamName="jolt"
					id="releasesDeleteModal"
				>
					<DeleteModal
						projectKey={projectKey}
						version={{ id: version.versionId, name: version.name }}
						onError={() => {
							reportError({
								metaId: 'jiraProjectVersionsDeleteModal',
								errorMessage: 'Failed to delete the version',
								messageId:
									'project-versions.ui.table.actions.show-flag.error.delete-modal.on-error.show-flag.error',
								title: formatMessage(messages.deleteFailedTitle),
								description: formatMessage(messages.deleteFailedDescription),
							});
						}}
						onSuccess={(successParams) => {
							showFlag({
								type: 'success',
								title: formatMessage(messages.deleteTitle, { name: version.name }),
								messageId:
									'project-versions.ui.table.actions.show-flag.success.delete-modal.on-success.show-flag.success',
								messageType: 'transactional',
							});

							updateStoreOnDelete(successParams.movedRelatedIssuesTo);
						}}
						onCancel={() => setIsDeleteVersionModalOpen(false)}
						onOpened={noop}
					/>
				</JSErrorBoundary>
			)}
		</ActionsCellWrapper>
	);
};

export default ActionsCell;

const spinnerContainerStyles = xcss({
	height: 'space.025',
	width: 'space.025',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonGroupItem = styled.div({
	paddingTop: token('space.050'),
	paddingRight: token('space.050'),
	paddingBottom: token('space.050'),
	paddingLeft: token('space.050'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ActionsCellWrapper = styled.div({
	display: 'flex',
	flexDirection: 'row-reverse',
});
